import { useState, useEffect } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Loader2, CreditCard, Lock } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import api from '@/services/api'

// Load Stripe with the publishable key. Prefer the build-time env, but fall back to fetching
// the key from the backend's public settings at runtime (so it works without a frontend rebuild
// and always uses the configured live/test key). Cached after the first load.
let _stripePromise: Promise<Stripe | null> | null = null
// Exported so other Stripe surfaces (e.g. the card-on-file mandate page) reuse THIS
// single loader instead of adding a second loadStripe() call — see memory
// fix_stripe_single_loader: two loaders caused "Stripe.js loaded more than once".
export async function getStripe(): Promise<Stripe | null> {
  if (_stripePromise) return _stripePromise
  _stripePromise = (async () => {
    let key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined
    if (!key) {
      try {
        const { data } = await api.get('/admin/settings/public')
        key = data?.STRIPE_PUBLISHABLE_KEY
      } catch {
        key = undefined
      }
    }
    return key ? loadStripe(key) : null
  })()
  return _stripePromise
}

interface StripePaymentFormProps {
  clientSecret: string
  amount: number
  onSuccess: (paymentIntent: any) => void
  onError?: (error: string) => void
}

function StripePaymentForm({
  clientSecret: _clientSecret,
  amount,
  onSuccess,
  onError,
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required',
      })

      if (error) {
        setErrorMessage(error.message || 'Payment failed')
        if (onError) onError(error.message || 'Payment failed')
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent)
      } else if (paymentIntent) {
        // Resolved inline but not yet 'succeeded' (e.g. 'processing' — async settlement that
        // finishes in seconds). This is NOT a failure: never leave the customer staring at a
        // silent form. Hand off to /payment-success, which polls until it's confirmed and
        // shows the result. (Redirect-based methods like Klarna already go there directly.)
        window.location.assign(`/payment-success?payment_intent=${encodeURIComponent(paymentIntent.id)}`)
        return
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Payment failed')
      if (onError) onError(err.message || 'Payment failed')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border border-blue-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Secure Payment</h3>
            <p className="text-sm text-gray-600">
              Total: <span className="font-bold">£{amount.toFixed(2)}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Lock className="w-3 h-3" />
          <span>256-bit SSL encryption • PCI DSS compliant</span>
        </div>
      </div>

      {/* Payment Element includes Card, Apple Pay, Google Pay */}
      <div className="p-4 bg-white rounded-xl border-2 border-gray-200">
        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                email: '',
              },
            },
          }}
        />
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700 text-sm font-medium">{errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
          !stripe || isProcessing
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
        }`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5" />
            Pay £{amount.toFixed(2)}
          </span>
        )}
      </button>

      {/* Payment Methods Info */}
      <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
        <span>We accept:</span>
        <div className="flex gap-2">
          <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold">VISA</div>
          <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold">Mastercard</div>
          <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold">Amex</div>
          <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold">Apple Pay</div>
          <div className="px-2 py-1 bg-gray-100 rounded text-xs font-semibold">Google Pay</div>
        </div>
      </div>
    </form>
  )
}

interface StripePaymentProps {
  clientSecret: string
  amount: number
  onSuccess: (paymentIntent: any) => void
  onError?: (error: string) => void
}

export function StripePayment({
  clientSecret,
  amount,
  onSuccess,
  onError,
}: StripePaymentProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    let active = true
    let settled = false
    // If Stripe.js is blocked (ad-blocker / privacy extension) it never resolves — surface
    // an actionable error after a timeout instead of an endless "Loading…" spinner.
    const timer = setTimeout(() => { if (active && !settled) setLoadFailed(true) }, 12000)
    getStripe()
      .then((s) => { settled = true; if (active) { setStripe(s); if (!s) setLoadFailed(true) } })
      .catch(() => { settled = true; if (active) setLoadFailed(true) })
    return () => { active = false; clearTimeout(timer) }
  }, [])

  if (!clientSecret) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Initializing payment...</p>
        </div>
      </Card>
    )
  }

  if (!stripe) {
    if (loadFailed) {
      return (
        <Card className="p-6">
          <div className="text-center space-y-3">
            <p className="font-semibold text-gray-900">We couldn't load the secure card form</p>
            <p className="text-sm text-gray-600">
              This is almost always an <b>ad-blocker or privacy/tracking-protection extension</b> blocking
              Stripe. Please pause it for this site and refresh, or try a different browser. You can also
              cancel and pay with <b>PayPal</b> instead.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              Refresh &amp; try again
            </button>
          </div>
        </Card>
      )
    }
    return (
      <Card className="p-8">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading payment form...</p>
        </div>
      </Card>
    )
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#3b82f6',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '0.75rem',
      },
      rules: {
        '.Input': {
          border: '2px solid #e5e7eb',
          boxShadow: 'none',
          padding: '12px',
        },
        '.Input:focus': {
          border: '2px solid #3b82f6',
          boxShadow: 'none',
        },
        '.Tab': {
          border: '2px solid #e5e7eb',
          boxShadow: 'none',
        },
        '.Tab:hover': {
          border: '2px solid #3b82f6',
        },
        '.Tab--selected': {
          border: '2px solid #3b82f6',
          backgroundColor: '#eff6ff',
        },
      },
    },
  }

  return (
    <div className="w-full">
      <Elements stripe={stripe} options={options}>
        <StripePaymentForm
          clientSecret={clientSecret}
          amount={amount}
          onSuccess={onSuccess}
          onError={onError}
        />
      </Elements>
    </div>
  )
}

// Utility function to create payment intent
export async function createStripePaymentIntent(
  amount: number,
  currency: string = 'gbp'
): Promise<{ clientSecret: string }> {
  // This should call your backend API
  const response = await fetch('/api/payments/create-intent', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      amount: Math.round(amount * 100), // Convert to cents
      currency,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to create payment intent')
  }

  return response.json()
}
